// Vendor portal (migrations 048, 049; shared/portal.ts), spread into `mutations` (shared/mutations.ts): what staff do for
// the portal. Who signs in is a vendor contact with `portal` ticked (vendor_contact.update, like Gets POs). The vendor's
// own changes aren't mutations: the portal's routes write them (server/src/portal/). Rules and implementations:
// server/src/mutations/portal.ts.
import { z } from "zod";
import { custom, update } from "./mutation-kit.ts";
import { confirmInput, newDateInput, noteInput, progressInput, shipmentInput } from "./order-updates.ts";

export const portalMutations = {
  /**
   * A link that signs this contact in once within a week (a new one ends the ones before it). Returns { token,
   * expires_at }: the link is /v/sign-in#<token>, for staff to send them; only its hash is kept. Send sign-in link
   * (POST /api/vendor-contacts/:id/sign-in-email) makes one and emails it.
   */
  "vendor_contact.sign_in_link": custom(z.object({ id: z.uuid() })),
  /**
   * A vendor's product becomes their link to an HHL part: their SKU, name, notes, photos and stock on the link (made, or
   * the one there filled in; price, MOQ and lead time only where the link has none), and the product goes. Returns
   * { vendor_item_id }.
   */
  "vendor_product.link": custom(z.object({ id: z.uuid(), item_id: z.uuid() })),
  /** HHL leaves a product alone ('ignored', still the vendor's), or takes it back to decide later ('new'). */
  "vendor_product.set_status": update("vendor_product", z.object({ status: z.enum(["new", "ignored"]) })),
  /**
   * Vendor prices (migration 054) --- HHL accepts a price a vendor proposed: it goes onto their link to the part (the
   * price, quantity breaks and currency; MOQ and lead time where they gave them), so onto new POs.
   */
  "vendor_price.accept": custom(z.object({ id: z.uuid() })),
  /** HHL declines it, with a word back the vendor sees. The link keeps the price it had. */
  "vendor_price.decline": custom(z.object({ id: z.uuid(), reply: z.string().trim().max(1000) })),
  /**
   * Order updates (migration 055) --- what a vendor says about an order, entered by HHL for a vendor not on the portal
   * (the portal's people say the same there): each one goes on the order's timeline as from HHL. `update_id` is the
   * page's own, so a retry makes it once.
   */
  "po.vendor_confirm": custom(confirmInput.extend({ id: z.uuid(), update_id: z.uuid() })),
  "po.vendor_progress": custom(progressInput.extend({ id: z.uuid(), update_id: z.uuid() })),
  "po.vendor_new_date": custom(newDateInput.extend({ id: z.uuid(), update_id: z.uuid() })),
  "po.vendor_note": custom(noteInput.extend({ id: z.uuid(), update_id: z.uuid() })),
  /** A shipment of the PO's goods (`id` is the shipment's own): the PO is Shipped and follows its tracking. */
  "po_shipment.add": custom(shipmentInput.extend({ id: z.uuid(), po_id: z.uuid() })),
  /** A shipment entered by mistake: its quantities come off the lines again. Not once it's received. */
  "po_shipment.remove": custom(z.object({ id: z.uuid() })),
  /** Someone at HHL opened the PO: its vendors' updates aren't new any more (the badge on Purchase orders). */
  "po_update.seen": custom(z.object({ po_id: z.uuid() })),
  /**
   * Catalog upload (migration 054) --- where reading an upload stands and what came of it (the vendor sees both):
   * 'reading' while it's being read, 'done' or 'failed' with a line on what came of it.
   */
  "portal_upload.handle": custom(
    z.object({ id: z.uuid(), status: z.enum(["new", "reading", "done", "failed"]), result: z.string().trim().max(4000).optional() }),
  ),
  /**
   * Draft products on a vendor's side from their catalog: made, or the one with that SKU of theirs filled in (still
   * 'new' for HHL to link). A SKU already on one of their parts is left alone and listed. Returns what was made,
   * changed and skipped.
   */
  "vendor_product.draft": custom(
    z.object({
      vendor_id: z.uuid(),
      upload_id: z.uuid().nullable().optional(),
      products: z
        .array(
          z.object({
            vendor_sku: z.string().trim().max(100),
            name: z.string().trim().min(1).max(300),
            family: z.string().trim().max(200).optional(),
            variant: z.string().trim().max(200).optional(),
            info: z.string().trim().max(5000).optional(),
            unit_price: z.number().nonnegative().nullable().optional(),
            currency: z.string().trim().toUpperCase().length(3).optional(),
            price_breaks: z
              .array(z.object({ min_qty: z.number().positive(), unit_price: z.number().nonnegative() }))
              .max(10)
              .optional(),
            moq: z.number().nonnegative().optional(),
            lead_time_days: z.number().int().nonnegative().nullable().optional(),
          }),
        )
        .min(1)
        .max(500),
    }),
  ),
};
